import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../lib/axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLang, LanguageBar } from '../../i18n/cda';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Progress } from '../../components/ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { toast } from 'sonner';
import JazminChat from '../../components/cda/JazminChat';
import { CdaLogo, ShareBadge } from '../../components/cda/CdaBrand';
import {
  PAYMENT_URL, CDA_COVER_SHEETS_URL, RESOURCE_LINKS, FAMILY_SERVICE_CDA, PRICING, CDA_SETTINGS, CDA_PROGRAMS,
  SUBJECT_AREAS, FREE_TRAININGS, PD_SPECIALISTS, APPLICATION_STEPS, CDA_FEE,
  FUNDING_OPTIONS, OBSERVATION_CHECKLIST, JOURNEY_STEPS,
} from '../../data/cdaContent';
import { scoreToBand } from '../../data/workforceContent';
import {
  CheckCircle, ExternalLink, Lock, LogOut, Upload, Star,
  Award, Heart, FileText, ArrowRight, Sparkles, Check, Trash2, Plus, Building2, LayoutDashboard,
} from 'lucide-react';

const CandidateJourney = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLang();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && user.role !== 'candidate' && user.role !== 'admin') { navigate('/dashboard'); return; }
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const res = await axios.get('/cda/progress');
      setProgress(res.data);
    } catch (e) {
      toast.error('Could not load your journey');
    } finally {
      setLoading(false);
    }
  };

  const save = async (patch) => {
    setProgress((prev) => ({ ...prev, ...patch }));
    try {
      const res = await axios.put('/cda/progress', { data: patch });
      setProgress(res.data);
    } catch (e) {
      toast.error('Could not save. Please try again.');
    }
  };

  const toggleStepComplete = (stepId) => {
    const done = progress.steps_completed || [];
    const next = done.includes(stepId) ? done.filter((s) => s !== stepId) : [...done, stepId];
    save({ steps_completed: next });
  };

  const isStepDone = (id) => (progress?.steps_completed || []).includes(id);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#fbf8f3]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0d7d6f]"></div></div>;
  }

  // ---------------- Subscription gate ----------------
  if (!progress.subscribed) {
    return <Paywall onConfirm={() => { toast.success('🎉'); fetchProgress(); }} onLogout={() => { logout(); navigate('/cda'); }} name={user?.name} isAdmin={user?.role === 'admin'} navigate={navigate} />;
  }

  const completedCount = (progress.steps_completed || []).length;
  const overall = Math.round((completedCount / JOURNEY_STEPS.length) * 100);

  return (
    <div className="min-h-screen bg-[#fbf8f3] pb-24">
      <LanguageBar />
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <CdaLogo size={34} />
          <div className="flex items-center gap-3">
            {user?.role === 'admin' && (
              <button 
                onClick={() => navigate('/cda-candidates')} 
                className="flex items-center gap-1.5 text-sm font-medium bg-[#0d7d6f] text-white px-3 py-1.5 rounded-lg hover:bg-[#0a6358] transition-colors" 
                data-testid="admin-dashboard-link"
              >
                <LayoutDashboard className="w-4 h-4" /> Admin Panel
              </button>
            )}
            <button onClick={() => { logout(); navigate('/cda'); }} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="journey-logout">
              <LogOut className="w-4 h-4" /> {t('Log out')}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
        {/* Progress hero */}
        <div className="bg-gradient-to-br from-[#0d7d6f] to-[#0a6358] rounded-2xl p-6 text-white mb-6">
          <p className="text-emerald-100 text-sm">{t('Welcome back,')}</p>
          <h1 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>{user?.name} 👋</h1>
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span>{t('Your CDA journey')}</span>
            <span className="font-semibold">{completedCount} {t('of')} {JOURNEY_STEPS.length} {t('steps')}</span>
          </div>
          <div className="h-3 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${overall}%` }} data-testid="overall-progress-bar"></div>
          </div>
          {overall === 100 && <p className="mt-3 text-sm">{t("🎓 You've completed every step. Congratulations, you're a CDA!")}</p>}
        </div>

        {/* Shareable badge */}
        <div className="mb-6">
          <ShareBadge />
        </div>

        {/* Steps */}
        <Accordion type="single" collapsible defaultValue="eligibility" className="space-y-3" data-testid="journey-steps">
          {JOURNEY_STEPS.map((step, idx) => (
            <AccordionItem key={step.id} value={step.id} className="bg-white rounded-xl border border-slate-200 px-4 overflow-hidden" data-testid={`step-${step.id}`}>
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex items-center gap-3 text-left">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-sm font-bold ${isStepDone(step.id) ? 'bg-[#0d7d6f] text-white' : 'bg-slate-100 text-slate-500'}`}>
                    {isStepDone(step.id) ? <Check className="w-5 h-5" /> : idx + 1}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{t(step.title)}</p>
                    <p className="text-xs text-slate-500">{t(step.subtitle)}</p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-5 pt-1">
                <StepBody stepId={step.id} progress={progress} save={save} navigate={navigate} />
                <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-3">
                  <Button
                    onClick={() => toggleStepComplete(step.id)}
                    variant={isStepDone(step.id) ? 'outline' : 'default'}
                    className={isStepDone(step.id) ? '' : 'bg-[#0d7d6f] hover:bg-[#0a6358]'}
                    data-testid={`complete-${step.id}`}
                  >
                    {isStepDone(step.id) ? <><CheckCircle className="w-4 h-4 mr-2" /> {t('Completed')}</> : <>{t('Mark step complete')} <ArrowRight className="w-4 h-4 ml-2" /></>}
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </main>

      <JazminChat />
    </div>
  );
};

// ============================ Paywall ============================
const Paywall = ({ onConfirm, onLogout, name, isAdmin, navigate }) => {
  const { t, lang } = useLang();
  const [confirming, setConfirming] = useState(false);
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await axios.post('/cda/subscribe-confirm', { 
        language: lang, 
        newsletter_opted_in: newsletterOptIn 
      });
      setShowWelcome(true);
    } catch (e) {
      console.error(e);
      setConfirming(false);
    }
  };

  // Jazmin Welcome Message after confirmation
  if (showWelcome) {
    return (
      <div className="min-h-screen bg-[#fbf8f3] flex flex-col">
        <LanguageBar />
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8 max-w-md w-full text-center" data-testid="jazmin-welcome">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#0d7d6f] to-[#0a6358] flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
              J
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {lang === 'es' ? '¡Felicidades!' : 'Congratulations!'} 🎉
            </h1>
            <p className="text-slate-600 mb-4">
              {lang === 'es' 
                ? `Acabas de dar un gran paso, ${name?.split(' ')[0] || 'amiga'}. Soy Jazmin, y estaré aquí animándote en cada paso del camino.`
                : `You just took a big step, ${name?.split(' ')[0] || 'friend'}. I'm Jazmin, and I'll be here cheering you on every step of the way.`
              }
            </p>
            <p className="text-slate-600 mb-6">
              {lang === 'es'
                ? 'Este viaje no siempre es fácil, pero no estás sola — yo creo en ti. 💚'
                : "This journey isn't always easy, but you're not alone — I believe in you. 💚"
              }
            </p>
            <Button 
              onClick={() => { setShowWelcome(false); onConfirm(); }} 
              className="w-full h-12 rounded-full bg-[#0d7d6f] hover:bg-[#0a6358] text-base"
              data-testid="jazmin-welcome-continue"
            >
              {lang === 'es' ? '¡Vamos a comenzar!' : "Let's get started!"}
            </Button>
            <p className="text-xs text-slate-400 mt-4">— Jazmin, {lang === 'es' ? 'Tu Coach CDA' : 'Your CDA Coach'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fbf8f3] flex flex-col">
      <LanguageBar />
      <header className="px-5 sm:px-10 py-4 flex items-center justify-between">
        <CdaLogo size={34} />
        <div className="flex items-center gap-3">
          {isAdmin && (
            <button 
              onClick={() => navigate('/cda-candidates')} 
              className="flex items-center gap-1.5 text-sm font-medium bg-[#0d7d6f] text-white px-3 py-1.5 rounded-lg hover:bg-[#0a6358] transition-colors" 
              data-testid="paywall-admin-link"
            >
              <LayoutDashboard className="w-4 h-4" /> Admin Panel
            </button>
          )}
          <button onClick={onLogout} className="text-sm text-slate-500 hover:text-slate-800">{t('Log out')}</button>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full bg-[#0d7d6f]/10 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-[#0d7d6f]" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('Unlock your CDA journey')}</h1>
          <p className="text-slate-600 text-sm mb-6">{t('Hi')} {name?.split(' ')[0] || 'there'}! {t('Subscribe to unlock your full step-by-step roadmap, trackers, binder builder, and Jazmin, your AI coach.')}</p>

          <div className="bg-[#fbf8f3] rounded-xl p-5 mb-4 border border-slate-100">
            <div className="flex items-end justify-center gap-1 mb-1">
              <span className="text-4xl font-bold text-slate-900">{PRICING.monthlyPrice}</span>
              <span className="text-slate-500 mb-1">/ {t(PRICING.monthlyLabel).toLowerCase()}</span>
            </div>
            <p className="text-xs text-slate-400 mt-3">{t(PRICING.note)}</p>
          </div>

          {/* Promo code */}
          <div className="bg-emerald-50 border border-dashed border-emerald-300 rounded-xl p-3 mb-5" data-testid="promo-code">
            <p className="text-sm text-emerald-900">
              🎟️ {t('Use code')} <span className="font-bold tracking-wide bg-white border border-emerald-300 rounded px-2 py-0.5">{PRICING.promoCode}</span> {t('at checkout for 50% off your first month.')}
            </p>
          </div>

          <a href={PAYMENT_URL} target="_blank" rel="noopener noreferrer" data-testid="paywall-pay-link">
            <Button className="w-full h-12 rounded-full bg-[#c4622d] hover:bg-[#a8501f] text-base mb-3">
              {t('Subscribe securely')} <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </a>
          <p className="text-xs text-slate-400 mb-5">{t('Payment is processed securely by Stripe. A new tab will open.')}</p>

          {/* Newsletter opt-in */}
          <label className="flex items-start gap-3 text-left mb-5 cursor-pointer" data-testid="newsletter-optin">
            <input 
              type="checkbox" 
              checked={newsletterOptIn} 
              onChange={(e) => setNewsletterOptIn(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 text-[#0d7d6f] focus:ring-[#0d7d6f]"
            />
            <span className="text-sm text-slate-600">
              {lang === 'es' 
                ? 'Envíame consejos mensuales, recursos y motivación en mi camino CDA'
                : 'Send me monthly tips, resources, and encouragement on my CDA journey'
              }
            </span>
          </label>

          <Button onClick={handleConfirm} variant="outline" className="w-full h-11" data-testid="paywall-confirm-button" disabled={confirming}>
            {confirming ? t('Verifying…') : t("I've completed my payment — continue")}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================ Step bodies ============================
const StepBody = ({ stepId, progress, save, navigate }) => {
  switch (stepId) {
    case 'eligibility': return <EligibilityStep progress={progress} save={save} />;
    case 'setting': return <SettingStep progress={progress} save={save} />;
    case 'education': return <EducationStep progress={progress} save={save} />;
    case 'free_training': return <FreeTrainingStep progress={progress} save={save} />;
    case 'workforce': return <WorkforceStep progress={progress} navigate={navigate} />;
    case 'placement': return <PlacementStep progress={progress} save={save} />;
    case 'experience': return <ExperienceStep progress={progress} save={save} />;
    case 'apply': return <ApplyStep progress={progress} save={save} />;
    case 'funding': return <FundingStep progress={progress} save={save} />;
    case 'observation': return <ObservationStep progress={progress} save={save} navigate={navigate} />;
    case 'credential': return <CredentialStep progress={progress} save={save} />;
    case 'feedback': return <FeedbackStep />;
    default: return null;
  }
};

const Pill = ({ active, children, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${active ? 'bg-[#0d7d6f] text-white border-[#0d7d6f]' : 'bg-white text-slate-700 border-slate-200 hover:border-[#0d7d6f]/40'}`}>
    {children}
  </button>
);

const ELIG_CDA_OPTIONS = [
  { id: 'exploring', title: "I'm just getting started", note: "Exploring the CDA and figuring out my path." },
  { id: 'earning', title: "I'm earning my CDA now", note: "I'm currently enrolled in a CDA program today — through an agency, family service, or an online program." },
  { id: 'binder', title: "I finished my classes", note: "I just need to build my binder and get observed." },
  { id: 'have', title: "I already have my CDA", note: "I'm renewing or supporting others." },
];

const EligibilityStep = ({ progress, save }) => {
  const { t } = useLang();
  const e = progress.eligibility || {};
  const setE = (patch) => save({ eligibility: { ...e, ...patch } });
  return (
    <div className="space-y-5">
      <div>
        <p className="font-medium text-slate-800 mb-2">{t('Are you currently a teacher at a licensed child care center?')}</p>
        <div className="flex gap-2">
          <Pill active={e.is_teacher === true} onClick={() => setE({ is_teacher: true })} testid="elig-teacher-yes">{t('Yes')}</Pill>
          <Pill active={e.is_teacher === false} onClick={() => setE({ is_teacher: false })} testid="elig-teacher-no">{t('Not yet')}</Pill>
        </div>
        {e.is_teacher === false && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-2">{t('Your 480 experience hours must be completed at a center. Jazmin can help you find a position — just ask!')}</p>
        )}
      </div>
      <div>
        <p className="font-medium text-slate-800 mb-2">{t('Where are you in your CDA journey?')}</p>
        <div className="grid sm:grid-cols-2 gap-2.5">
          {ELIG_CDA_OPTIONS.map((o) => (
            <button key={o.id} onClick={() => setE({ has_cda: o.id })} data-testid={`elig-cda-${o.id}`}
              className={`text-left p-3.5 rounded-xl border-2 transition-all ${e.has_cda === o.id ? 'border-[#0d7d6f] bg-[#0d7d6f]/5' : 'border-slate-200 hover:border-[#0d7d6f]/30'}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-900 text-sm">{t(o.title)}</span>
                {e.has_cda === o.id && <CheckCircle className="w-4 h-4 text-[#0d7d6f] shrink-0" />}
              </div>
              <p className="text-xs text-slate-500 mt-1">{t(o.note)}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const SettingStep = ({ progress, save }) => {
  const { t } = useLang();
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">{t('Your CDA is tied to the age group you work with. Pick the setting that matches your classroom.')}</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {CDA_SETTINGS.map((s) => (
          <button key={s.id} onClick={() => save({ cda_setting: s.id })} data-testid={`setting-${s.id}`}
            className={`text-left p-4 rounded-xl border-2 transition-all ${progress.cda_setting === s.id ? 'border-[#0d7d6f] bg-[#0d7d6f]/5' : 'border-slate-200 hover:border-[#0d7d6f]/30'}`}>
            <div className="flex items-center justify-between">
              <span className="font-semibold text-slate-900">{t(s.name)}</span>
              {progress.cda_setting === s.id && <CheckCircle className="w-5 h-5 text-[#0d7d6f]" />}
            </div>
            <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded mt-1 inline-block">{t(s.ages)}</span>
            <p className="text-xs text-slate-500 mt-2">{t(s.desc)}</p>
          </button>
        ))}
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <Checkbox checked={!!progress.bilingual} onCheckedChange={(c) => save({ bilingual: !!c })} data-testid="setting-bilingual" />
        {t('Add the Bilingual (Spanish/English) specialization')}
      </label>
    </div>
  );
};

const EducationStep = ({ progress, save }) => {
  const { t } = useLang();
  const log = progress.education_log || [];
  const total = log.reduce((s, e) => s + (Number(e.hours) || 0), 0);
  const pct = Math.min((total / 120) * 100, 100);

  const persist = (next) => save({ education_log: next, education_hours: next.reduce((s, e) => s + (Number(e.hours) || 0), 0) });
  const addEntry = (entry) => persist([...log, { id: Date.now().toString(), ...entry }]);
  const removeEntry = (id) => persist(log.filter((e) => e.id !== id));

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
        <Checkbox className="mt-0.5" checked={!!progress.class_completed} onCheckedChange={(c) => save({ class_completed: !!c })} data-testid="education-skip" />
        <div>
          <p className="text-sm font-medium text-blue-900">{t("I've already completed my CDA class")}</p>
          <p className="text-xs text-blue-700">{t("Check this if your coursework is done — you don't need to enroll again. Just log your hours below and keep your transcript handy.")}</p>
        </div>
      </div>

      {!progress.class_completed && (
        <>
          <p className="text-sm text-slate-600">{t("You need 120 clock hours of formal early childhood education, with at least 10 hours in each of the 8 CDA subject areas. Here's where to take your classes (most are paid):")}</p>
          <div className="space-y-2">
            {CDA_PROGRAMS.map((p) => (
              <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`program-${p.name.replace(/\s+/g, '-').toLowerCase()}`}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200 hover:border-[#0d7d6f]/40">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-[#0d7d6f]">{p.name}</p>
                    {p.badge && <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full">{t(p.badge)}</span>}
                  </div>
                  <p className="text-xs text-slate-500">{t(p.note)}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-slate-400 shrink-0" />
              </a>
            ))}
          </div>

          {/* Free option — Family Service (listed separately) */}
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4" data-testid="family-service-cda">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 bg-emerald-600 text-white rounded-full">{t('Free option')}</span>
              <p className="font-semibold text-slate-900">{FAMILY_SERVICE_CDA.name}</p>
            </div>
            <p className="text-sm text-slate-600">{t('Free CDA classes — with application requirements.')}</p>
            <p className="text-sm text-slate-600">{t('13-week class — meets twice a week for 3 hours.')}</p>
            <div className="text-xs text-slate-500 mt-2 space-y-0.5">
              <p>📍 {FAMILY_SERVICE_CDA.address}</p>
              <p>📞 {t('CDA classes')}: {FAMILY_SERVICE_CDA.phone}</p>
            </div>
            <a href={FAMILY_SERVICE_CDA.url} target="_blank" rel="noopener noreferrer" className="inline-block mt-3" data-testid="family-service-link">
              <Button size="sm" className="bg-[#0d7d6f] hover:bg-[#0a6358]">{t('Register / learn more')} <ExternalLink className="w-3.5 h-3.5 ml-1.5" /></Button>
            </a>
          </div>

          <details className="bg-slate-50 rounded-lg border border-slate-200 p-3">
            <summary className="text-sm font-medium text-slate-800 cursor-pointer">{t('The 8 CDA subject areas (10+ hrs each)')}</summary>
            <ul className="grid sm:grid-cols-2 gap-1.5 text-sm mt-2">
              {SUBJECT_AREAS.map((a, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-600"><span className="text-[#0d7d6f] font-bold">{i + 1}.</span> {t(a)}</li>
              ))}
            </ul>
          </details>
        </>
      )}

      {/* Progress toward 120 */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700">{t('Training hours logged')}</span>
          <span className="text-sm text-slate-500">{total} / 120 {t('hrs')}</span>
        </div>
        <Progress value={pct} className="h-3" data-testid="education-progress-bar" />
        <p className="text-xs text-slate-500 mt-2">{total >= 120 ? t('✓ 120 hours complete — great work!') : `${120 - total} ${t('hours to go')}`}</p>
      </div>

      <TrainingLog log={log} onAdd={addEntry} onRemove={removeEntry} />
    </div>
  );
};

const TrainingLog = ({ log, onAdd, onRemove }) => {
  const { t } = useLang();
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ name: '', date: today, hours: '', agency: '' });
  const submit = () => {
    if (!form.name.trim() || !form.hours) { toast.error(t('Add a training name and hours')); return; }
    onAdd({ name: form.name.trim(), date: form.date, hours: Number(form.hours), agency: form.agency.trim() });
    setForm({ name: '', date: today, hours: '', agency: '' });
    toast.success(t('Added to your training log'));
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="training-log">
      <p className="font-semibold text-slate-900 flex items-center gap-2"><FileText className="w-4 h-4 text-[#0d7d6f]" /> {t('Your Training Log')}</p>
      <p className="text-xs text-slate-500 mb-3">{t('Add each training: title, date, hours, and the agency/provider. This becomes your official record for your binder.')}</p>

      {log.length === 0 ? (
        <p className="text-sm text-slate-400 py-2">{t('No trainings logged yet — add your first below.')}</p>
      ) : (
        <div className="space-y-2 mb-3">
          {log.map((e) => (
            <div key={e.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border border-slate-200" data-testid="training-entry">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{e.name}</p>
                <p className="text-xs text-slate-500">{e.hours} {t('hrs')} • {e.date}{e.agency ? ` • ${e.agency}` : ''}</p>
              </div>
              <button onClick={() => onRemove(e.id)} className="p-1.5 text-slate-400 hover:text-red-500 shrink-0" data-testid="training-remove" aria-label="Remove">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-2">
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('Training name')} data-testid="training-name" />
        <Input value={form.agency} onChange={(e) => setForm({ ...form, agency: e.target.value })} placeholder={t('Agency / provider')} data-testid="training-agency" />
        <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="training-date" />
        <Input type="number" min="0" step="0.5" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder={t('Hours')} data-testid="training-hours" />
      </div>
      <Button onClick={submit} size="sm" className="mt-3 bg-[#0d7d6f] hover:bg-[#0a6358]" data-testid="training-add">
        <Plus className="w-4 h-4 mr-1.5" /> {t('Add to log')}
      </Button>
    </div>
  );
};

const FreeTrainingStep = ({ progress, save }) => {
  const { t } = useLang();
  const done = progress.free_trainings || [];
  const toggle = (name) => save({ free_trainings: done.includes(name) ? done.filter((n) => n !== name) : [...done, name] });
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">{t('Most centers are required to provide these trainings — and many count toward your 120 hours. Use these free resources to fill any gaps, then add each one to your Training Log in the previous step.')}</p>
      <div className="bg-[#0d7d6f]/5 border border-[#0d7d6f]/20 rounded-lg p-3 text-sm text-slate-700">
        💡 {t("Tip: Your local public library card often unlocks free online courses (Gale/Udemy, Universal Class) covering the CDA subject areas for the pathway you're enrolled in.")}
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="resource-callout">
        <p className="font-semibold text-amber-900 mb-2">📚 {t('Recommended class catalogs & resources')}</p>
        <div className="space-y-2">
          {RESOURCE_LINKS.map((r) => (
            <a key={r.url} href={r.url} target="_blank" rel="noopener noreferrer" data-testid={`resource-${r.name.replace(/\s+/g, '-').toLowerCase().slice(0, 18)}`}
              className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-amber-200 hover:border-amber-400">
              <div className="min-w-0">
                <p className="font-medium text-slate-800">{t(r.name)}</p>
                <p className="text-xs text-slate-500">{t(r.note)}</p>
              </div>
              <ExternalLink className="w-4 h-4 text-amber-600 shrink-0" />
            </a>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        {FREE_TRAININGS.map((item) => (
          <div key={item.name} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200">
            <Checkbox checked={done.includes(item.name)} onCheckedChange={() => toggle(item.name)} data-testid={`free-${item.name.replace(/\s+/g, '-').toLowerCase().slice(0, 20)}`} />
            <a href={item.url} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-between group">
              <div className="min-w-0">
                <p className="font-medium text-slate-800 group-hover:text-[#0d7d6f]">{t(item.name)}</p>
                <p className="text-xs text-slate-500">{t(item.hours)}{item.free ? ` • ${t('FREE')}` : ''}{item.area ? ` • ${t(item.area)}` : ''}</p>
              </div>
              <ExternalLink className="w-4 h-4 text-slate-400 shrink-0" />
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

const WorkforceStep = ({ progress, navigate }) => {
  const { t } = useLang();
  const band = progress.readiness_score != null ? scoreToBand(progress.readiness_score) : null;
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">{t('Completed your training but never worked in a childcare center? This module builds the workplace skills employers look for — professional appearance, communication, reliability, interviews, resume help, and more — plus your Professional Readiness Score.')}</p>
      {band && (
        <div className="rounded-xl border p-4 flex items-center gap-3" style={{ borderColor: band.color + '55', background: band.color + '11' }} data-testid="workforce-band">
          <span className="text-3xl">{band.emoji}</span>
          <div>
            <p className="font-semibold text-slate-900">{progress.readiness_score}/100 — {band.label}</p>
            <p className="text-xs text-slate-600">{band.note}</p>
          </div>
        </div>
      )}
      <Button onClick={() => navigate('/cda/workforce-readiness')} className="bg-[#0d7d6f] hover:bg-[#0a6358]" data-testid="open-workforce">
        {band ? t('Review the module & retake') : t('Open the Workforce Readiness module')} <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
};

const PlacementStep = ({ progress, save }) => {
  const { t } = useLang();
  const [form, setForm] = useState({ city: '', availability: '', phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.city.trim()) { toast.error(t('Please enter your city or ZIP code')); return; }
    setSubmitting(true);
    try {
      await axios.post('/cda/placement-request', {
        city: form.city, availability: form.availability,
        phone: form.phone, notes: form.notes, cda_setting: progress.cda_setting,
      });
      toast.success(t("Request received! We'll be in touch."));
      save({ placement_requested: true });
    } catch (e) {
      toast.error(t('Something went wrong. Please try again.'));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">{t('Finished your classes but need a center for your 480 hours? Care Comply helps connect you with a learning center where you can be hired and paid while you complete your hours — and apply for your observation under that center.')}</p>
      {progress.placement_requested ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center" data-testid="placement-confirmed">
          <CheckCircle className="w-8 h-8 text-[#0d7d6f] mx-auto mb-2" />
          <p className="font-semibold text-slate-900">{t('Your request is in! 🎉')}</p>
          <p className="text-sm text-slate-600">{t('Care Comply will reach out to help match you with a center near you. You can ask Jazmin anything in the meantime.')}</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <p className="font-semibold text-slate-900">{t('Request a center match')}</p>
          <div>
            <label className="text-sm font-medium text-slate-700">{t('City or ZIP code')}</label>
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5" placeholder="San Antonio, 78212" data-testid="placement-city" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">{t('Availability')}</label>
            <div className="flex flex-wrap gap-2">
              <Pill active={form.availability === 'full_time'} onClick={() => setForm({ ...form, availability: 'full_time' })} testid="avail-full">{t('Full-time')}</Pill>
              <Pill active={form.availability === 'part_time'} onClick={() => setForm({ ...form, availability: 'part_time' })} testid="avail-part">{t('Part-time')}</Pill>
              <Pill active={form.availability === 'either'} onClick={() => setForm({ ...form, availability: 'either' })} testid="avail-either">{t('Either')}</Pill>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">{t('Phone (optional)')}</label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" placeholder="(210) 555-0123" data-testid="placement-phone" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">{t('Anything else? (optional)')}</label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" placeholder={t('Your CDA setting, schedule needs, etc.')} data-testid="placement-notes" />
          </div>
          <Button onClick={submit} disabled={submitting} className="bg-[#0d7d6f] hover:bg-[#0a6358]" data-testid="placement-submit">
            {submitting ? t('Please wait...') : t('Request a center match')}
          </Button>
        </div>
      )}
      <p className="text-xs text-slate-500">{t('Are you a childcare center looking for motivated CDA candidates? Reach out to partner with Care Comply.')}</p>
    </div>
  );
};

const ExperienceStep = ({ progress, save }) => {
  const { t } = useLang();
  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
        <strong>{t('Note:')}</strong> {t('For the CDA, the 480 hours are professional work / clinical experience hours working directly with children — not observation hours. The observation (Verification Visit) is a separate, one-day visit later in the process.')}
      </div>
      <p className="text-sm text-slate-600">{t('You need 480 hours of hands-on experience with children in your CDA setting, completed within the past 3 years (roughly 12 full-time weeks). Track your progress here.')}</p>
      <HoursTracker label={t('Work experience hours')} value={progress.experience_hours || 0} max={480} field="experience_hours" save={save} testid="experience-hours" />
      <p className="text-xs text-slate-500">{t('Tip: your center director will verify these hours during your CDA application — keep your work schedule records.')}</p>
    </div>
  );
};

const ApplyStep = ({ progress, save }) => {
  const { t } = useLang();
  return (
    <div className="space-y-5">
      <div>
        <p className="font-medium text-slate-800 mb-2">{t("Let's get your application on file")}</p>
        <p className="text-sm text-slate-600 mb-3"><a href="https://www.cdacouncil.org/en/credentials/how-to-apply/" target="_blank" rel="noopener noreferrer" className="text-[#0d7d6f] hover:underline font-medium">cdacouncil.org</a> — {t("Here's what happens during the application:")}</p>
        <ol className="space-y-2">
          {APPLICATION_STEPS.map((s, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
              <span className="w-6 h-6 rounded-full bg-[#0d7d6f]/10 text-[#0d7d6f] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
              {t(s)}
            </li>
          ))}
        </ol>
      </div>

      <div>
        <p className="font-medium text-slate-800 mb-2">{t('Select your PD Specialist (San Antonio area)')}</p>
        <p className="text-xs text-slate-500 mb-3">{t("During the application you'll choose a Professional Development Specialist for your Verification Visit. They'll contact you to schedule the observation at your center.")}</p>
        {PD_SPECIALISTS.map((pd) => (
          <button key={pd.number} onClick={() => save({ pd_specialist: pd.number })} data-testid={`pd-${pd.number}`}
            className={`w-full text-left p-4 rounded-xl border-2 transition-all ${progress.pd_specialist === pd.number ? 'border-[#0d7d6f] bg-[#0d7d6f]/5' : 'border-slate-200 hover:border-[#0d7d6f]/30'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-slate-900">{pd.name}</p>
                <p className="text-xs text-slate-500">{t('CDA Professional Development Specialist')} • {pd.region}</p>
              </div>
              <span className="text-xs px-2.5 py-1 bg-white border border-[#0d7d6f]/30 text-[#0d7d6f] rounded-full font-medium">#{pd.number}</span>
            </div>
          </button>
        ))}
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
        <Checkbox checked={!!progress.application_submitted} onCheckedChange={(c) => save({ application_submitted: !!c })} data-testid="apply-submitted" />
        {t("I've submitted my CDA application on cdacouncil.org")}
      </label>
    </div>
  );
};

const FundingStep = ({ progress, save }) => {
  const { t } = useLang();
  return (
    <div className="space-y-4">
      <div className="bg-[#fbf8f3] border border-slate-200 rounded-lg p-4 text-center">
        <p className="text-sm text-slate-600">{t('CDA assessment fee (online)')}</p>
        <p className="text-3xl font-bold text-slate-900">{CDA_FEE}</p>
        <p className="text-xs text-slate-400">{t('Paid to the Council for Professional Recognition when you submit. Confirm current pricing on cdacouncil.org.')}</p>
      </div>

      {/* Step 1: Ask your employer first */}
      <div className="bg-white border-2 border-[#0d7d6f]/20 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <Building2 className="w-5 h-5 text-[#0d7d6f]" />
          <p className="font-semibold text-slate-900">{t('First, ask your employer 💼')}</p>
        </div>
        <p className="text-sm text-slate-600 mb-3">{t('Many centers will pay the CDA fee as part of professional development. Ask your center director: is there a professional development budget that could cover my CDA assessment fee?')}</p>
        <p className="text-sm font-medium text-slate-800 mb-2">{t('Can your employer help with the fee?')}</p>
        <div className="flex flex-wrap gap-2">
          <Pill active={progress.employer_funding === 'yes'} onClick={() => save({ employer_funding: 'yes' })} testid="employer-yes">{t('Yes, my center will pay')}</Pill>
          <Pill active={progress.employer_funding === 'asking'} onClick={() => save({ employer_funding: 'asking' })} testid="employer-asking">{t("I'm going to ask")}</Pill>
          <Pill active={progress.employer_funding === 'no'} onClick={() => save({ employer_funding: 'no' })} testid="employer-no">{t('No / need another way')}</Pill>
        </div>
        {progress.employer_funding === 'asking' && (
          <p className="text-xs text-[#0d7d6f] bg-[#0d7d6f]/5 border border-[#0d7d6f]/20 rounded-lg p-2 mt-2">{t("Great — frame it as an investment: a credentialed teacher raises your center's quality rating. Ask Jazmin to help you word the request!")}</p>
        )}
      </div>

      {/* Step 2: Other ways to fund */}
      {progress.employer_funding !== 'yes' && (
        <>
          <p className="text-sm text-slate-600 font-medium">{t("If your center can't cover it, here are other ways to fund your CDA:")}</p>
          {FUNDING_OPTIONS.map((f) => (
            <div key={f.title} className="p-4 bg-white rounded-xl border border-slate-200">
              <div className="flex items-center gap-2 mb-1.5">
                {f.icon === 'heart' ? <Heart className="w-5 h-5 text-[#c4622d]" /> : <Award className="w-5 h-5 text-[#0d7d6f]" />}
                <p className="font-semibold text-slate-900">{t(f.title)}</p>
              </div>
              <p className="text-sm text-slate-600 mb-3">{t(f.desc)}</p>
              <a href={f.url} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" data-testid={`funding-${f.icon}`}>{t(f.cta)} <ExternalLink className="w-3.5 h-3.5 ml-1.5" /></Button>
              </a>
            </div>
          ))}
        </>
      )}

      <label className="flex items-center gap-2 text-sm text-slate-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
        <Checkbox checked={!!progress.fee_paid} onCheckedChange={(c) => save({ fee_paid: !!c })} data-testid="funding-paid" />
        {t('My assessment fee is covered / paid')}
      </label>
    </div>
  );
};

const ObservationStep = ({ progress, save, navigate }) => {
  const { t } = useLang();
  const checks = progress.observation_ready || {};
  const toggle = (id) => save({ observation_ready: { ...checks, [id]: !checks[id] } });
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">{t('Your PD Specialist will spend a full day at your center watching you put your training into action. Get your binder and your classroom ready:')}</p>
      <div className="space-y-2">
        {OBSERVATION_CHECKLIST.map((c) => (
          <label key={c.id} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200 cursor-pointer">
            <Checkbox className="mt-0.5" checked={!!checks[c.id]} onCheckedChange={() => toggle(c.id)} data-testid={`obs-${c.id}`} />
            <span className="text-sm text-slate-700">{t(c.label)}</span>
          </label>
        ))}
      </div>
      <div className="bg-[#0d7d6f]/5 border border-[#0d7d6f]/20 rounded-xl p-4">
        <p className="font-medium text-slate-900 flex items-center gap-2"><FileText className="w-4 h-4 text-[#0d7d6f]" /> {t('Binder section cover sheets')}</p>
        <p className="text-xs text-slate-500 mb-3">{t('Print one cover for each labeled tab of your 3-inch binder.')}</p>
        <div className="flex flex-wrap gap-2">
          <a href={CDA_COVER_SHEETS_URL} target="_blank" rel="noopener noreferrer">
            <Button size="sm" className="bg-[#0d7d6f] hover:bg-[#0a6358]" data-testid="cover-sheets-drive">{t('Open the CDA cover sheets')} <ExternalLink className="w-3.5 h-3.5 ml-1.5" /></Button>
          </a>
          <Button onClick={() => navigate('/cda/binder-covers')} size="sm" variant="outline" data-testid="open-binder-covers">{t('Printable templates')}</Button>
        </div>
      </div>
    </div>
  );
};

const CredentialStep = ({ progress, save }) => {
  const { t } = useLang();
  const [uploading, setUploading] = useState(false);
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post('/cda/credential', fd, { headers: { 'Content-Type': undefined } });
      toast.success(t('Credential uploaded! 🎓 You did it!'));
      save({ credential_uploaded: true, observed: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Upload failed'));
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="space-y-4 text-center">
      {progress.credential_uploaded ? (
        <div className="py-6">
          <div className="w-16 h-16 rounded-full bg-[#0d7d6f]/10 flex items-center justify-center mx-auto mb-3">
            <Award className="w-8 h-8 text-[#0d7d6f]" />
          </div>
          <p className="font-bold text-slate-900 text-lg">{t('Your CDA credential is saved! 🎉')}</p>
          <p className="text-sm text-slate-600 mb-4">{t("Congratulations — you're officially a Child Development Associate. You're shining!")}</p>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/cda/credential/file?`} target="_blank" rel="noopener noreferrer" className="text-sm text-[#0d7d6f] hover:underline">{t('View uploaded file')}</a>
          <div className="mt-3">
            <label className="text-xs text-slate-400 cursor-pointer hover:underline">
              {t('Replace file')}
              <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={onUpload} data-testid="credential-replace-input" />
            </label>
          </div>
        </div>
      ) : (
        <>
          <p className="text-sm text-slate-600">{t("Once you've been observed and receive your CDA credential, upload a digital copy here to keep it safe.")}</p>
          <label className="block border-2 border-dashed border-slate-300 rounded-xl p-8 cursor-pointer hover:border-[#0d7d6f]/50 transition-colors" data-testid="credential-upload-label">
            <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-700">{uploading ? t('Uploading…') : t('Click to upload your CDA certificate')}</p>
            <p className="text-xs text-slate-400 mt-1">{t('PDF or image, up to 10MB')}</p>
            <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={onUpload} disabled={uploading} data-testid="credential-upload-input" />
          </label>
        </>
      )}
    </div>
  );
};

const FeedbackStep = () => {
  const { t } = useLang();
  const [rating, setRating] = useState(0);
  const [helpful, setHelpful] = useState('');
  const [suggestions, setSuggestions] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const submit = async () => {
    if (!rating) { toast.error(t('Please pick a star rating')); return; }
    try {
      await axios.post('/cda/feedback', { rating, helpful_most: helpful, suggestions, would_recommend: rating >= 4 });
      setSubmitted(true);
      toast.success(t('Thank you! Your story helps the next teacher. 💛'));
    } catch (e) {
      toast.error(t('Could not submit feedback'));
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-6">
        <Sparkles className="w-10 h-10 text-[#c4622d] mx-auto mb-3" />
        <p className="font-bold text-slate-900 mb-1">{t("You're amazing!")}</p>
        <p className="text-sm text-slate-600 mb-2">{t('Thank you for sharing your story. Keep shining! 💛')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">{t('How was your Care Comply experience? Your feedback shapes the journey for the next teacher.')}</p>
      <div className="flex gap-1 justify-center" data-testid="feedback-stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}>
            <Star className={`w-8 h-8 ${n <= rating ? 'fill-[#c4622d] text-[#c4622d]' : 'text-slate-300'}`} />
          </button>
        ))}
      </div>
      <div>
        <label className="text-sm font-medium text-slate-700">{t('What helped you most?')}</label>
        <Input value={helpful} onChange={(e) => setHelpful(e.target.value)} className="mt-1.5" placeholder={t('The binder builder, Jazmin, the trackers...')} data-testid="feedback-helpful" />
      </div>
      <div>
        <label className="text-sm font-medium text-slate-700">{t('Anything we could improve?')}</label>
        <Textarea value={suggestions} onChange={(e) => setSuggestions(e.target.value)} className="mt-1.5" placeholder={t('Your ideas...')} data-testid="feedback-suggestions" />
      </div>
      <Button onClick={submit} className="bg-[#0d7d6f] hover:bg-[#0a6358]" data-testid="feedback-submit">{t('Submit feedback')}</Button>
    </div>
  );
};

// Shared hours tracker (uncontrolled input — resets via key when value changes)
const HoursTracker = ({ label, value, max, field, save, testid }) => {
  const { t } = useLang();
  const pct = Math.min((value / max) * 100, 100);
  const commit = (e) => {
    const v = Math.max(0, Math.min(Number(e.target.value) || 0, max + 1000));
    if (v !== value) save({ [field]: v });
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <div className="flex items-center gap-2">
          <Input key={value} type="number" min="0" max={max} defaultValue={value} onBlur={commit}
            className="w-20 h-8 text-right" data-testid={`${testid}-input`} />
          <span className="text-sm text-slate-500">/ {max} {t('hrs')}</span>
        </div>
      </div>
      <Progress value={pct} className="h-3" data-testid={`${testid}-bar`} />
      {value >= max ? (
        <p className="text-xs text-[#0d7d6f] font-medium mt-2">{t('✓ Requirement met! Great work.')}</p>
      ) : (
        <p className="text-xs text-slate-500 mt-2">{max - value} {t('hours to go')}</p>
      )}
    </div>
  );
};

export default CandidateJourney;
